import React from 'react';
import DecimalInput from './DecimalInput';
import IntegerInput from './IntegerInput';
import FileAttachment from './FileAttachment';

const DEFAULT_COLUMN_TYPE = 'text';

const getColumnWidth = (column) => (column.type === 'file' ? 320 : 180);
const getColumnClassName = (column) =>
  column.type === 'file'
    ? 'w-80 min-w-64 max-w-[22rem] overflow-hidden px-3 py-2 align-top'
    : 'w-44 min-w-40 max-w-[13.75rem] px-3 py-2 align-top';

const normalizeColumns = (columns) =>
  Array.isArray(columns)
    ? columns
        .map((column, index) => ({
          id: typeof column?.id === 'string' && column.id.trim() ? column.id.trim() : `coluna_${index + 1}`,
          label: typeof column?.label === 'string' && column.label.trim() ? column.label.trim() : `Coluna ${index + 1}`,
          type: typeof column?.type === 'string' && column.type ? column.type : DEFAULT_COLUMN_TYPE,
          required: Boolean(column?.required),
          options: Array.isArray(column?.options) ? column.options.map((option) => String(option).trim()).filter(Boolean) : [],
        }))
        .filter((column) => column.id && column.label)
    : [];

const createEmptyRow = (columns) => {
  const nextRow = {};
  columns.forEach((column) => {
    nextRow[column.id] = '';
  });
  return nextRow;
};

export default function TableField({
  field,
  value,
  onChange,
  readOnly = false,
  inputClassName = '',
  onUploadFile,
  getCellUpload,
  getRowPendingUploadCount,
  getTablePendingUploadCount,
}) {
  const columns = normalizeColumns(field?.tableColumns);
  const rows = Array.isArray(value) ? value : [];
  const showInitialEmptyRow = !readOnly && rows.length === 0 && columns.length > 0;
  const visibleRows = showInitialEmptyRow ? [createEmptyRow(columns)] : rows;
  const tablePendingUploadCount = getTablePendingUploadCount?.() || 0;
  const hasTablePendingUploads = tablePendingUploadCount > 0;
  const tableMinWidth = columns.reduce((total, column) => total + getColumnWidth(column), readOnly ? 0 : 96);
  const attachmentContext = [
    field?.label,
    field?.id,
    field?.moduleBinding?.moduleField,
    ...columns.map((column) => column.label),
  ]
    .filter(Boolean)
    .join(' ')
    .toLocaleLowerCase('pt-BR');
  const isAttachmentTable =
    columns.some((column) => column.type === 'file') &&
    (field?.moduleBinding?.moduleField === 'files.documents' || /anex|arquivo|document/.test(attachmentContext));

  const updateCell = (rowIndex, columnId, nextValue) => {
    const nextRows = [...rows];
    const existingRow = nextRows[rowIndex] && typeof nextRows[rowIndex] === 'object' ? nextRows[rowIndex] : {};
    nextRows[rowIndex] = {
      ...existingRow,
      [columnId]: nextValue,
    };
    onChange(nextRows);
  };

  const addRow = () => {
    const baseRows = showInitialEmptyRow ? visibleRows : rows;
    onChange([...baseRows, createEmptyRow(columns)]);
  };

  const removeRow = (rowIndex) => {
    if (hasTablePendingUploads || getRowPendingUploadCount?.(rowIndex) > 0) return;
    const nextRows = rows.filter((_, index) => index !== rowIndex);
    onChange(nextRows);
  };

  const renderCellContent = (rowIndex, column) => {
    const cellValue = visibleRows[rowIndex]?.[column.id] ?? '';
    const isInitialPlaceholderRow = showInitialEmptyRow && rowIndex === 0;
    const isRequiredCell = column.required && (!isInitialPlaceholderRow || Boolean(field?.required));

    if (column.type === 'file') {
      const cellUpload = getCellUpload?.(rowIndex, column.id);
      const isUploadingCell = Boolean(cellUpload);
      return readOnly ? (
        <div className="max-w-full min-w-0 overflow-hidden">
          <FileAttachment file={cellValue} compact />
        </div>
      ) : (
        <div className="max-w-full min-w-0 space-y-2 overflow-hidden">
          {cellValue && <FileAttachment file={cellValue} compact />}
          <input
            type="file"
            className="block w-full max-w-full min-w-0 cursor-pointer rounded-md border border-dashed border-slate-300 bg-white px-2 py-2 text-xs text-slate-700"
            disabled={isUploadingCell}
            onChange={(event) => {
              const selectedFile = event.target.files?.[0];
              if (!selectedFile || !onUploadFile) return;
              onUploadFile({ rowIndex, column, file: selectedFile });
              event.target.value = '';
            }}
            required={isRequiredCell && !cellValue}
          />
          {isUploadingCell && (
            <p className="text-xs text-blue-600">
              {cellUpload.fileName} — {cellUpload.status === 'processing' ? 'Processando...' : `${cellUpload.percent || 0}%`}
            </p>
          )}
        </div>
      );
    }

    if (column.type === 'list' || column.type === 'select') {
      return (
        <select
          value={cellValue}
          onChange={(event) => updateCell(rowIndex, column.id, event.target.value)}
          disabled={readOnly}
          required={isRequiredCell}
          className={inputClassName}
        >
          <option value="">Selecione</option>
          {(column.options || []).map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      );
    }

    if (column.type === 'number') {
      return (
        <IntegerInput
          value={cellValue}
          onChange={(event) => updateCell(rowIndex, column.id, event.target.value)}
          readOnly={readOnly}
          required={isRequiredCell}
          className={inputClassName}
        />
      );
    }

    if (column.type === 'decimal') {
      return (
        <DecimalInput
          decimalPlaces={2}
          value={cellValue}
          onChange={(event) => updateCell(rowIndex, column.id, event.target.value)}
          readOnly={readOnly}
          required={isRequiredCell}
          className={inputClassName}
        />
      );
    }

    return (
      <input
        type={column.type === 'date' ? 'date' : column.type === 'email' ? 'email' : 'text'}
        value={cellValue}
        onChange={(event) => updateCell(rowIndex, column.id, event.target.value)}
        readOnly={readOnly}
        required={isRequiredCell}
        className={inputClassName}
      />
    );
  };

  if (columns.length === 0) {
    return <p className="text-xs text-amber-600">Defina ao menos uma coluna para usar o campo de tabela.</p>;
  }

  if (isAttachmentTable) {
    return (
      <div className="w-full space-y-3">
        {readOnly && rows.length === 0 && (
          <div className="w-full rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-center text-sm text-slate-500">
            Nenhum anexo adicionado.
          </div>
        )}
        {visibleRows.map((row, rowIndex) => (
          <div
            key={`attachment-row-${rowIndex}`}
            className={`grid w-full grid-cols-1 gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm ${readOnly ? 'lg:grid-cols-[minmax(180px,0.7fr)_minmax(280px,1.6fr)]' : 'lg:grid-cols-[minmax(180px,0.7fr)_minmax(280px,1.6fr)_auto]'}`}
          >
            {columns.map((column) => (
              <label key={`${rowIndex}-${column.id}`} className="min-w-0 space-y-1.5">
                <span className="block text-xs font-semibold text-slate-600">
                  {column.label}
                  {column.required && <span className="text-red-500"> *</span>}
                </span>
                <div className="min-w-0 w-full">{renderCellContent(rowIndex, column)}</div>
              </label>
            ))}
            {!readOnly && !(showInitialEmptyRow && rowIndex === 0) && (
              <div className="flex items-end lg:justify-end">
                <button
                  type="button"
                  onClick={() => removeRow(rowIndex)}
                  disabled={hasTablePendingUploads || getRowPendingUploadCount?.(rowIndex) > 0}
                  className="w-full rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 lg:w-auto"
                >
                  Remover
                </button>
              </div>
            )}
          </div>
        ))}
        {!readOnly && (
          <button
            type="button"
            onClick={addRow}
            className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100"
          >
            Adicionar anexo
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="w-full space-y-2">
      <div className="w-full overflow-x-auto rounded-md border border-slate-200">
        <table className="min-w-full table-fixed divide-y divide-slate-200 text-sm" style={{ minWidth: `${tableMinWidth}px` }}>
          <colgroup>
            {columns.map((column) => (
              <col key={column.id} style={{ width: `${getColumnWidth(column)}px` }} />
            ))}
            {!readOnly && <col style={{ width: '96px' }} />}
          </colgroup>
          <thead className="bg-slate-50">
            <tr>
              {columns.map((column) => (
                <th key={column.id} className="truncate px-3 py-2 text-left font-semibold text-slate-700" title={column.label}>
                  {column.label}
                  {column.required && <span className="text-red-500"> *</span>}
                </th>
              ))}
              {!readOnly && <th className="w-24 px-3 py-2 text-right font-semibold text-slate-700">Ações</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            {readOnly && rows.length === 0 && (
              <tr>
                <td colSpan={columns.length + (readOnly ? 0 : 1)} className="px-3 py-4 text-center text-xs text-slate-500">
                  Nenhuma linha adicionada.
                </td>
              </tr>
            )}
            {visibleRows.map((row, rowIndex) => (
              <tr key={`table-row-${rowIndex}`}>
                {columns.map((column) => (
                  <td key={`${rowIndex}-${column.id}`} className={getColumnClassName(column)}>
                    {renderCellContent(rowIndex, column)}
                  </td>
                ))}
                {!readOnly && (
                  <td className="w-24 px-3 py-2 text-right align-top">
                    {!(showInitialEmptyRow && rowIndex === 0) && (
                      <button
                        type="button"
                        onClick={() => removeRow(rowIndex)}
                        disabled={hasTablePendingUploads || getRowPendingUploadCount?.(rowIndex) > 0}
                        className="rounded border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Remover
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!readOnly && (
        <button
          type="button"
          onClick={addRow}
          className="rounded border border-blue-200 px-3 py-1.5 text-xs font-semibold text-blue-600 hover:bg-blue-50"
        >
          Adicionar linha
        </button>
      )}
    </div>
  );
}
